import { useCallback, useEffect, useRef, useState } from 'react';
import { drawMandelbrotFractal, applyPanAndRenderEdges } from './fractal_math';
import { getWebGLContext, drawMandelbrotFractalWebGL, needsDeepZoom, disposeWebGLPanCache, disposeWebGLZoomCache,
  presentWebGLZoomTransition, retainWebGLZoomTransition, discardWebGLZoomTransition,
  presentWebGLZoomSource } from './fractalWebGL';
import { useFractalWorker } from './useFractalWorker';
import { presentPanTiles } from './fractalPixels';
import { canCertifyCpuPalette, planExactCpuPan } from './cpuExactPan';
import { workerRenderRoute } from './cpuRenderRoute';
import { getCpuExactFrame, setCpuExactFrame, clearCpuExactFrame } from './cpuExactFrameCache';
import { cameraFromRanges } from './fractalCamera';
import { MAX_ITERATIONS, SMOOTHING_STEPS } from './fractalIteration';

const ZOOM_GPU_BUDGET_MS = 24;
const SHALLOW_ZOOM_FADE_MS = 220;
const SHALLOW_GESTURE_FADE_MS = 180;
const ZOOM_SMALL_CANVAS_PIXELS = 500000;
// The viewport canvas can reach 1920 × 1500 pixels. Admit that size only after
// its own full-resolution GPU frame has completed within the gesture budget.
const ZOOM_MAX_EXACT_PIXELS = 1920 * 1500;

function sameRanges(first, second) {
  return first && second && ['xRange', 'yRange'].every(axis =>
    first[axis]?.length === 2 && second[axis]?.length === 2 &&
    first[axis][0] === second[axis][0] && first[axis][1] === second[axis][1]);
}

function easeZoomCorrection(progress) {
  return progress * progress * (3 - 2 * progress);
}

function planCanvasFromCamera(source, target) {
  if (!source || !target) return false;
  const oldX = source.xRange[1] - source.xRange[0];
  const oldY = source.yRange[1] - source.yRange[0];
  const newX = target.xRange[1] - target.xRange[0];
  const newY = target.yRange[1] - target.yRange[0];
  if (![oldX, oldY, newX, newY].every(value => Number.isFinite(value) && value > 0)) return false;
  const scaleX = oldX / newX;
  const scaleY = oldY / newY;
  const offsetX = (source.xRange[0] - target.xRange[0]) / newX;
  const offsetY = (source.yRange[0] - target.yRange[0]) / newY;
  if (![scaleX, scaleY, offsetX, offsetY].every(Number.isFinite)) return null;
  return { scaleX, scaleY, offsetX, offsetY };
}

function scaleCanvasFromCamera(canvas, source, target) {
  const projection = planCanvasFromCamera(source, target);
  if (!projection) return false;
  const { scaleX, scaleY, offsetX, offsetY } = projection;
  canvas.style.transformOrigin = '0 0';
  canvas.style.transform = `translate(${offsetX * 100}%, ${offsetY * 100}%) scale(${scaleX}, ${scaleY})`;
  return true;
}

function drawProjectedCanvas(context, image, source, target, width, height) {
  const projection = planCanvasFromCamera(source, target);
  if (!projection) return false;
  const { scaleX, scaleY, offsetX, offsetY } = projection;
  context.drawImage(image, offsetX * width, offsetY * height,
    scaleX * width, scaleY * height);
  return true;
}

function releaseZoomFence(active) {
  if (!active.sync) return;
  try { active.gl.deleteSync(active.sync); } catch (error) { /* Context may be lost. */ }
  active.sync = null;
}

function isCanvasVisible(canvas) {
  if (!canvas || document.hidden) return false;
  const rect = canvas.getBoundingClientRect();
  return rect.bottom >= 0 && rect.top <= window.innerHeight;
}

// The old renderer remains event-driven (only visible canvases receive render
// events). Deep numerical work has its own controller and never reaches here.
export function useFractalRenderer(canvasRef, { initialRanges, colorFunc, colorParams, paletteParams,
  cpuReusePaletteArray = false, cpuExactPanReuse = false, cpuInteriorDispatch = false,
  preferredRenderer, onFallback, onCpuFrameReady }) {
  const [canvasEpoch, setCanvasEpoch] = useState(0);
  const [revision, setRevision] = useState(0);
  const [recovering, setRecovering] = useState(false);
  const forceCanvas = useRef(false);
  const requested = useRef(initialRanges);
  const completed = useRef(initialRanges);
  const completedImage = useRef(null);
  const rendered = useRef(false);
  const pan = useRef(false);
  const settledPan = useRef(false);
  const cpuExactOwner = useRef(Symbol('cpu-exact-frame'));
  const cpuPreview = useRef(false);
  const cpuFade = useRef(null);
  const cpuRetainedPreview = useRef(false);
  const enabled = useRef(true);
  const generation = useRef(0);
  const zoomTransition = useRef(null);
  const zoomGestureMode = useRef(null);
  const gpuProbe = useRef(null);
  const gpuQuality = useRef({ canvas: null, width: 0, height: 0, status: 'unknown', sampledAt: 0 });
  const mounted = useRef(false);
  const paramsRef = useRef(colorParams);
  paramsRef.current = colorParams;
  const colorFuncRef = useRef(colorFunc);
  colorFuncRef.current = colorFunc;
  const onCpuFrameReadyRef = useRef(onCpuFrameReady);
  onCpuFrameReadyRef.current = onCpuFrameReady;
  const { renderFull, renderPan, renderExactPan, cancelRender, shouldUseWorker } = useFractalWorker();

  const stopCPUFade = useCallback((retainAt = null) => {
    const active = cpuFade.current;
    if (!active) return false;
    cpuFade.current = null;
    if (active.frame != null) cancelAnimationFrame(active.frame);
    const canvas = active.canvas;
    let retained = false;
    if (retainAt && canvas === canvasRef.current && canvas.width === active.width &&
        canvas.height === active.height) {
      try {
        const snapshot = document.createElement('canvas');
        snapshot.width = active.width;
        snapshot.height = active.height;
        const context = snapshot.getContext('2d');
        const target = canvas.getContext('2d');
        if (context?.drawImage && target?.drawImage) {
          context.fillStyle = '#0a0e18';
          context.fillRect(0, 0, snapshot.width, snapshot.height);
          if (!drawProjectedCanvas(context, canvas, completed.current, retainAt,
            snapshot.width, snapshot.height)) throw new Error('CPU source projection unavailable');
          context.globalAlpha = active.opacity;
          if (!drawProjectedCanvas(context, active.preview, active.sourceRanges, retainAt,
            snapshot.width, snapshot.height)) throw new Error('CPU fade projection unavailable');
          context.globalAlpha = 1;
          target.clearRect?.(0, 0, canvas.width, canvas.height);
          target.drawImage(snapshot, 0, 0);
          canvas.style.transform = '';
          completed.current = retainAt;
          completedImage.current = { width: canvas.width, height: canvas.height,
            colorFunc: colorFuncRef.current };
          rendered.current = true;
          cpuPreview.current = false;
          cpuRetainedPreview.current = true;
          retained = true;
        }
      } catch (error) { /* An exact redraw remains available. */ }
    }
    active.wrapper.remove();
    return retained;
  }, [canvasRef]);

  const freezeCPUFade = useCallback(() => {
    const active = cpuFade.current;
    if (!active || active.frozen) return;
    if (active.frame != null) cancelAnimationFrame(active.frame);
    active.frame = null;
    active.frozen = true;
  }, []);

  const prepareCPUFade = useCallback((canvas, ranges) => {
    const image = completedImage.current;
    if (pan.current || !image || image.width !== canvas.width || image.height !== canvas.height ||
        image.colorFunc !== colorFuncRef.current || sameRanges(completed.current, ranges) &&
        !cpuFade.current) return null;
    const projection = planCanvasFromCamera(completed.current, ranges);
    if (!projection || !canvas.parentElement) return null;
    const previous = cpuFade.current;
    if (previous && (previous.canvas !== canvas || previous.width !== canvas.width ||
        previous.height !== canvas.height)) stopCPUFade();
    const active = cpuFade.current;
    const wrapper = document.createElement('div');
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.dataset.cpuZoomFade = '';
    Object.assign(wrapper.style, { position: 'absolute', inset: '0', overflow: 'hidden',
      pointerEvents: 'none', zIndex: '1', opacity: '1' });
    const preview = document.createElement('canvas');
    preview.width = canvas.width;
    preview.height = canvas.height;
    preview.style.cssText = canvas.style.cssText;
    preview.style.pointerEvents = 'none';
    wrapper.appendChild(preview);
    const context = preview.getContext('2d');
    if (!context?.drawImage) return null;
    let sourceRanges = completed.current;
    try {
      if (active) {
        // Flatten the two visible layers only when new exact pixels are ready.
        // Input frames continue to project both existing layers directly.
        context.fillStyle = '#0a0e18';
        context.fillRect(0, 0, canvas.width, canvas.height);
        drawProjectedCanvas(context, canvas, completed.current, ranges,
          canvas.width, canvas.height);
        context.globalAlpha = active.opacity;
        drawProjectedCanvas(context, active.preview, active.sourceRanges, ranges,
          canvas.width, canvas.height);
        context.globalAlpha = 1;
        sourceRanges = ranges;
        preview.style.transform = '';
      } else {
        context.drawImage(canvas, 0, 0);
        scaleCanvasFromCamera(preview, sourceRanges, ranges);
      }
    } catch (error) {
      wrapper.remove();
      if (active) stopCPUFade();
      return null;
    }
    try { canvas.parentElement.appendChild(wrapper); }
    catch (error) {
      wrapper.remove();
      if (active) stopCPUFade();
      return null;
    }
    if (active) {
      if (active.frame != null) cancelAnimationFrame(active.frame);
      active.wrapper.remove();
    }
    const next = { canvas, width: canvas.width, height: canvas.height, wrapper,
      preview, sourceRanges, targetRanges: ranges, opacity: 1, start: null,
      frame: null, frozen: false };
    cpuFade.current = next;
    return next;
  }, [stopCPUFade]);

  const animateCPUFade = useCallback(active => {
    const animate = time => {
      if (cpuFade.current !== active || active.frozen) return;
      if (active.canvas !== canvasRef.current || active.canvas.width !== active.width ||
          active.canvas.height !== active.height) {
        stopCPUFade();
        return;
      }
      if (active.start === null) {
        active.start = time;
        active.frame = requestAnimationFrame(animate);
        return;
      }
      const progress = Math.min(1, Math.max(0, (time - active.start) / SHALLOW_ZOOM_FADE_MS));
      active.opacity = 1 - easeZoomCorrection(progress);
      active.wrapper.style.opacity = String(active.opacity);
      if (progress === 1) stopCPUFade();
      else active.frame = requestAnimationFrame(animate);
    };
    active.frame = requestAnimationFrame(animate);
  }, [canvasRef, stopCPUFade]);

  const commitGPUFrame = useCallback((canvas, ranges) => {
    const frame = canvas._fractalZoomCache?.frame;
    completed.current = ranges;
    completedImage.current = { width: frame?.width ?? canvas.width, height: frame?.height ?? canvas.height,
      colorFunc: colorFuncRef.current };
    rendered.current = true;
    setRecovering(false);
  }, []);

  const stopZoomTransition = useCallback((promote = true, retainVisible = false) => {
    const active = zoomTransition.current;
    if (!active) return false;
    zoomTransition.current = null;
    cancelAnimationFrame(active.frame);
    releaseZoomFence(active);
    const matchingCanvas = active.canvas === canvasRef.current && active.canvas.width === active.width &&
      active.canvas.height === active.height;
    if (retainVisible && matchingCanvas && retainWebGLZoomTransition(active.canvas)) {
      // This texture is the pixels actually shown during the fade. Its camera
      // is useful for the next projection, but it is not an exact history frame.
      const frame = active.canvas._fractalZoomCache?.frame;
      if (frame) {
        completed.current = { xRange: frame.xRange, yRange: frame.yRange };
        completedImage.current = { width: frame.width, height: frame.height,
          colorFunc: colorFuncRef.current };
      }
      rendered.current = true;
      return true;
    }
    const cache = active.canvas._fractalZoomCache;
    if (retainVisible && matchingCanvas && cache?.sourceTexture && cache?.frame &&
        !cache.gl?.isContextLost?.() && (cache.transition?.displayedBlend ?? 0) <= 0.5) {
      // If copying a mostly retained blend fails, its original source is still
      // closer to the visible pixels than an abrupt promotion to exact detail.
      discardWebGLZoomTransition(active.canvas);
      return false;
    }
    if (promote && matchingCanvas &&
        presentWebGLZoomTransition(active.canvas, 1)) {
      commitGPUFrame(active.canvas, active.ranges);
    } else discardWebGLZoomTransition(active.canvas);
    return false;
  }, [canvasRef, commitGPUFrame]);

  const stopGPUProbe = useCallback(() => {
    const active = gpuProbe.current;
    if (!active) return;
    gpuProbe.current = null;
    cancelAnimationFrame(active.frame);
    releaseZoomFence(active);
  }, []);

  // Sample completed GPU work without making a gesture wait for gl.finish().
  // A slow full frame keeps later gestures on the retained-image preview.
  const sampleGPUFrame = useCallback(canvas => {
    if (preferredRenderer !== 'webgl' || document.hidden || gpuProbe.current) return;
    const gl = canvas._fractalZoomCache?.gl;
    if (!gl?.fenceSync || !gl.clientWaitSync || !gl.deleteSync ||
        gl.SYNC_GPU_COMMANDS_COMPLETE == null) return;
    const prior = gpuQuality.current;
    if (prior.canvas !== canvas || prior.width !== canvas.width || prior.height !== canvas.height) {
      gpuQuality.current = { canvas, width: canvas.width, height: canvas.height,
        status: 'unknown', sampledAt: 0 };
    }
    const quality = gpuQuality.current;
    if (quality.sampledAt && performance.now() - quality.sampledAt < 500) return;
    const active = { gl, canvas, width: canvas.width, height: canvas.height,
      start: performance.now(), frame: null, sync: null };
    try {
      active.sync = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
      gl.flush?.();
    } catch (error) { releaseZoomFence(active); return; }
    if (!active.sync) return;
    gpuProbe.current = active;
    const poll = () => {
      if (gpuProbe.current !== active) return;
      if (canvas !== canvasRef.current || canvas.width !== active.width ||
          canvas.height !== active.height || document.hidden || gl.isContextLost?.()) {
        stopGPUProbe();
        return;
      }
      let status;
      try { status = gl.clientWaitSync(active.sync, 0, 0); } catch (error) { status = null; }
      const elapsed = performance.now() - active.start;
      if (status === gl.TIMEOUT_EXPIRED && elapsed < ZOOM_GPU_BUDGET_MS) {
        active.frame = requestAnimationFrame(poll);
        return;
      }
      stopGPUProbe();
      const completed = status === gl.ALREADY_SIGNALED || status === gl.CONDITION_SATISFIED;
      const fast = completed && elapsed <= ZOOM_GPU_BUDGET_MS;
      gpuQuality.current = { canvas, width: active.width, height: active.height,
        status: fast ? 'fast' : 'slow',
        sampledAt: performance.now() };
      // Keep the mode selected on the first gesture frame. The timing result
      // admits exact rendering for the next gesture, without swapping pixels
      // underneath a held preview gesture.
    };
    active.frame = requestAnimationFrame(poll);
  }, [canvasRef, preferredRenderer, stopGPUProbe]);

  const exactZoomGeometry = useCallback((canvas, ranges, allowRetained = false) => {
    if (preferredRenderer !== 'webgl') return false;
    const frame = canvas._fractalZoomCache?.frame;
    if (!frame || !canvas._fractalZoomCache?.gl || frame.width !== canvas.width ||
        frame.height !== canvas.height || frame.deepZoom || frame.exact === false && !allowRetained ||
        frame.paletteKey !== JSON.stringify(paramsRef.current || {}) ||
        needsDeepZoom(ranges.xRange, ranges.yRange, canvas.width, canvas.height,
          canvas._fractalDeepZoom, canvas._fractalFloatPrecision ?? 23)) return false;
    return true;
  }, [preferredRenderer]);

  const shouldRenderExactZoom = useCallback((canvas, ranges) => {
    if (!exactZoomGeometry(canvas, ranges)) return false;
    const quality = gpuQuality.current;
    const status = quality.canvas === canvas && quality.width === canvas.width &&
      quality.height === canvas.height ? quality.status : 'unknown';
    const pixels = canvas.width * canvas.height;
    return status !== 'slow' && (pixels <= ZOOM_SMALL_CANVAS_PIXELS ||
      status === 'fast' && pixels <= ZOOM_MAX_EXACT_PIXELS);
  }, [exactZoomGeometry]);

  const animateZoomTransition = useCallback((canvas, ranges) => {
    stopGPUProbe();
    const gl = canvas._fractalZoomCache?.gl;
    const exactGesture = !!canvas._fractalZoomCache?.transition?.interactiveExact;
    const active = { canvas, ranges, gl, exactGesture, width: canvas.width, height: canvas.height,
      start: null, gpuStart: performance.now(), frame: null, sync: null };
    if (gl?.fenceSync && gl.clientWaitSync && gl.deleteSync && gl.SYNC_GPU_COMMANDS_COMPLETE != null) {
      try {
        active.sync = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
        gl.flush?.();
      } catch (error) { releaseZoomFence(active); }
    }
    zoomTransition.current = active;
    const animate = time => {
      if (zoomTransition.current !== active) return;
      if (canvas !== canvasRef.current || canvas.width !== active.width || canvas.height !== active.height) {
        zoomTransition.current = null;
        releaseZoomFence(active);
        discardWebGLZoomTransition(canvas);
        rendered.current = false;
        return;
      }
      if (active.sync) {
        let status;
        try { status = gl.clientWaitSync(active.sync, 0, 0); } catch (error) { status = null; }
        if (status === gl.TIMEOUT_EXPIRED) {
          active.frame = requestAnimationFrame(animate);
          return;
        }
        const completed = status === gl.ALREADY_SIGNALED || status === gl.CONDITION_SATISFIED;
        gpuQuality.current = { canvas, width: active.width, height: active.height,
          status: completed && performance.now() - active.gpuStart <= ZOOM_GPU_BUDGET_MS
            ? 'fast' : 'slow', sampledAt: performance.now() };
        releaseZoomFence(active);
      }
      if (active.start === null) {
        // Show the retained bitmap for one presentation frame once detail is
        // ready, then start the fade on the next frame.
        active.start = time;
        if (!presentWebGLZoomTransition(canvas, 0)) {
          zoomTransition.current = null;
          return;
        }
        active.frame = requestAnimationFrame(animate);
        return;
      }
      const duration = active.exactGesture ? SHALLOW_GESTURE_FADE_MS : SHALLOW_ZOOM_FADE_MS;
      const progress = Math.min(1, Math.max(0, (time - active.start) / duration));
      // Start and end the correction gently while preserving its exact final pixels.
      const eased = easeZoomCorrection(progress);
      if (!presentWebGLZoomTransition(canvas, eased)) {
        zoomTransition.current = null;
        releaseZoomFence(active);
        return;
      }
      if (progress === 1) {
        zoomTransition.current = null;
        commitGPUFrame(canvas, ranges);
      } else active.frame = requestAnimationFrame(animate);
    };
    active.frame = requestAnimationFrame(animate);
  }, [canvasRef, commitGPUFrame, stopGPUProbe]);

  const fallback = useCallback((canvas, ranges) => {
    if (canvas !== canvasRef.current || forceCanvas.current) return;
    stopZoomTransition(false);
    forceCanvas.current = true;
    requested.current = onFallback?.() || ranges;
    completed.current = null;
    completedImage.current = null;
    rendered.current = false;
    pan.current = false;
    cpuPreview.current = false;
    setRecovering(true);
    setCanvasEpoch(value => value + 1);
  }, [canvasRef, onFallback, stopZoomTransition]);

  const drawGPU = useCallback((canvas, ranges, options) => {
    if (forceCanvas.current) return false;
    let ok = false;
    try {
      ok = drawMandelbrotFractalWebGL(canvas, ranges.xRange, ranges.yRange, paramsRef.current,
        options ? { ...options, twoFrameCache: true } : { twoFrameCache: true });
    } catch (error) {
      console.warn('WebGL render failed, falling back to canvas:', error);
    }
    if (!ok) fallback(canvas, ranges);
    else if (canvas._fractalZoomCache?.transition) {
      rendered.current = true;
      animateZoomTransition(canvas, ranges);
      setRecovering(false);
    } else if (canvas._fractalRenderStats?.reprojected) {
      // The visible target differs from the bitmap's exact source camera.
      rendered.current = true;
      setRecovering(false);
    } else {
      const frame = canvas._fractalZoomCache?.frame;
      commitGPUFrame(canvas, frame
        ? { xRange: frame.xRange, yRange: frame.yRange } : ranges);
      if (!options?.cachePan) sampleGPUFrame(canvas);
    }
    return ok;
  }, [animateZoomTransition, commitGPUFrame, fallback, sampleGPUFrame]);

  const present = useCallback((ranges, { interactive = false, pan: isPan = false, defer = false } = {}) => {
    const canvas = canvasRef.current;
    const previousRequest = requested.current;
    const waitForVisibility = defer || !isCanvasVisible(canvas);
    const activeFade = zoomTransition.current;
    // Repeated updates at the same camera should let the existing fade finish.
    if (activeFade && !isPan && !waitForVisibility &&
        activeFade.canvas === canvas && activeFade.width === canvas.width &&
        activeFade.height === canvas.height && sameRanges(activeFade.ranges, ranges)) {
      if (!interactive) zoomGestureMode.current = null;
      return;
    }
    const activeCPUFade = cpuFade.current;
    if (activeCPUFade && !activeCPUFade.frozen && !isPan &&
        !waitForVisibility && activeCPUFade.canvas === canvas &&
        sameRanges(activeCPUFade.targetRanges, ranges)) return;
    const retainedExactFade = !!activeFade?.exactGesture;
    const retainedFade = stopZoomTransition(!waitForVisibility, !waitForVisibility);
    enabled.current = true;
    requested.current = ranges;
    generation.current += 1;
    cancelRender();
    if (defer && canvas?._useWebGL) presentWebGLZoomSource(canvas);
    if (canvas?._useWebGL && !waitForVisibility && !forceCanvas.current) {
      stopCPUFade();
      if (retainedFade && interactive && !isPan) {
        zoomGestureMode.current = retainedExactFade ? 'exact' : 'preview';
      }
      if (interactive && !isPan && zoomGestureMode.current === null) {
        zoomGestureMode.current = shouldRenderExactZoom(canvas, ranges) ? 'exact' : 'preview';
      }
      if (!interactive) {
        const exactGesture = zoomGestureMode.current === 'exact';
        zoomGestureMode.current = null;
        const frame = canvas._fractalZoomCache?.frame;
        if (exactGesture && rendered.current && frame && !canvas._fractalZoomCache?.transition &&
            frame.width === canvas.width && frame.height === canvas.height &&
            frame.paletteKey === JSON.stringify(paramsRef.current || {}) && sameRanges(frame, ranges)) return;
      }
      const exactGesture = interactive && !isPan && zoomGestureMode.current === 'exact' &&
        exactZoomGeometry(canvas, ranges, retainedExactFade);
      if (interactive && !isPan && zoomGestureMode.current === 'exact' && !exactGesture) {
        zoomGestureMode.current = 'preview';
      }
      drawGPU(canvas, ranges, isPan && interactive ? { cachePan: true, pan: true } :
        interactive ? exactGesture ? { interactiveExact: true } : { interactiveZoom: true } : undefined);
    } else {
      if (!interactive) zoomGestureMode.current = null;
      if (waitForVisibility) stopCPUFade();
      else if (isPan && cpuFade.current) stopCPUFade(previousRequest);
      else if (cpuFade.current) freezeCPUFade();
      const image = completedImage.current;
      const sourceIsCurrent = canvas && image && image.width === canvas.width && image.height === canvas.height &&
        image.colorFunc === colorFuncRef.current;
      if (!canvas?._useWebGL && !isPan && !waitForVisibility && sourceIsCurrent &&
          !sameRanges(completed.current, ranges) &&
          scaleCanvasFromCamera(canvas, completed.current, ranges)) {
        const fade = cpuFade.current;
        if (fade) scaleCanvasFromCamera(fade.preview, fade.sourceRanges, ranges);
        // Both visible layers follow the camera immediately. The fade freezes
        // while new pixels are computed and resumes with the new exact frame.
        rendered.current = true;
        pan.current = false;
        cpuPreview.current = true;
        if (interactive) return;
      }
      if (interactive && !isPan && sourceIsCurrent && sameRanges(completed.current, ranges)) return;
      if (waitForVisibility && canvas) {
        canvas.style.transform = '';
        cpuPreview.current = false;
      }
      // Integer pixel copies are previews. A settled fractional pan must redraw
      // its exact camera instead of accumulating rounded copy offsets.
      pan.current = isPan && interactive;
      settledPan.current = isPan && !interactive;
      rendered.current = false;
      setRevision(value => value + 1);
    }
  }, [canvasRef, cancelRender, drawGPU, exactZoomGeometry, freezeCPUFade,
    shouldRenderExactZoom, stopCPUFade, stopZoomTransition]);

  const suspend = useCallback(() => {
    stopZoomTransition(true, true);
    zoomGestureMode.current = null;
    if (canvasRef.current?._useWebGL) presentWebGLZoomSource(canvasRef.current);
    else if (canvasRef.current) {
      stopCPUFade(requested.current);
      canvasRef.current.style.transform = '';
      cpuPreview.current = false;
    }
    enabled.current = false;
    generation.current += 1;
    cancelRender();
  }, [canvasRef, cancelRender, stopCPUFade, stopZoomTransition]);

  const restoreImage = useCallback((ranges, image) => {
    stopZoomTransition();
    stopCPUFade();
    zoomGestureMode.current = null;
    enabled.current = true;
    requested.current = completed.current = ranges;
    generation.current += 1;
    cancelRender();
    const context = canvasRef.current?.getContext('2d');
    if (image && context) {
      context.putImageData(image, 0, 0);
      canvasRef.current.style.transform = '';
      cpuPreview.current = false;
      cpuRetainedPreview.current = false;
      rendered.current = true;
      completedImage.current = { width: canvasRef.current.width, height: canvasRef.current.height, colorFunc: colorFuncRef.current };
      setRecovering(false);
      return true;
    }
    return false;
  }, [canvasRef, cancelRender, stopCPUFade, stopZoomTransition]);

  const canCaptureHistory = useCallback(ranges => {
    const canvas = canvasRef.current;
    const image = completedImage.current;
    return rendered.current && image && canvas && !cpuPreview.current &&
      !cpuFade.current && !cpuRetainedPreview.current &&
      canvas._fractalZoomCache?.frame?.exact !== false && !canvas._fractalZoomCache?.interactive &&
      !canvas._fractalZoomCache?.panPreview &&
      !canvas._fractalZoomCache?.transition && image.width === canvas.width && image.height === canvas.height &&
      image.colorFunc === colorFuncRef.current && ['xRange', 'yRange'].every(axis =>
        completed.current?.[axis].every((value, index) => value === ranges[axis][index]));
  }, [canvasRef]);

  const getPresentedSnapshot = useCallback(() => completedImage.current && completed.current
    ? cameraFromRanges(completed.current.xRange, completed.current.yRange).snapshot() : null, []);

  useEffect(() => {
    mounted.current = true;
    const canvas = canvasRef.current;
    const exactOwner = cpuExactOwner.current;
    const onContextLost = () => {
      // A WebGL canvas cannot switch to 2D after losing its context. Replace
      // it so the existing CPU renderer can redraw the current view.
      if (canvas._useWebGL) fallback(canvas, requested.current);
    };
    canvas.addEventListener('webglcontextlost', onContextLost);
    return () => {
      mounted.current = false;
      stopZoomTransition(false);
      stopCPUFade();
      stopGPUProbe();
      zoomGestureMode.current = null;
      generation.current += 1;
      cancelRender();
      clearCpuExactFrame(exactOwner);
      canvas.removeEventListener('webglcontextlost', onContextLost);
      disposeWebGLPanCache(canvas);
      disposeWebGLZoomCache(canvas);
    };
  }, [canvasRef, canvasEpoch, cancelRender, fallback, stopCPUFade,
    stopZoomTransition, stopGPUProbe]);

  useEffect(() => {
    stopZoomTransition();
    stopCPUFade();
    rendered.current = false;
    generation.current += 1;
    cancelRender();
    clearCpuExactFrame(cpuExactOwner.current);
  }, [colorFunc, colorParams, paletteParams, cpuReusePaletteArray, cancelRender, stopCPUFade, stopZoomTransition]);

  useEffect(() => {
    const canvas = canvasRef.current;
    let disposed = false;
    let running = false;
    const render = async () => {
      if (!enabled.current || rendered.current || running || disposed) return;
      running = true;
      const job = generation.current;
      const stale = () => disposed || !mounted.current || !enabled.current || job !== generation.current;
      const ranges = requested.current;
      if (!ranges) { running = false; return; }
      const workerColorParams = cpuExactPanReuse && paletteParams ? { ...colorParams } : colorParams;
      // Match the key to the frozen values sent to every worker tile. A
      // supplied palette object may be mutated in place during an async job.
      const paletteKeyAtStart = JSON.stringify(workerColorParams);
      const paletteCertifiable = cpuExactPanReuse && paletteParams &&
        canCertifyCpuPalette(workerColorParams);
      const paletteChanged = () => paletteCertifiable &&
        (!canCertifyCpuPalette(colorParams) ||
          paletteKeyAtStart !== JSON.stringify(colorParams));
      if (canvas._webGLDecided === undefined) {
        canvas._webGLDecided = true;
        canvas._useWebGL = preferredRenderer === 'webgl' && !forceCanvas.current && !!getWebGLContext(canvas);
      }
      if (canvas._useWebGL && !forceCanvas.current) {
        drawGPU(canvas, ranges);
        running = false;
        return;
      }
      // NumberCamera supplies fresh arrays, but direct hook callers may edit
      // their requested arrays while worker tiles are in flight. Give every
      // tile one immutable numerical grid and decline a stale live request.
      const workerRanges = paletteCertifiable ? Object.freeze({
        xRange: Object.freeze([ranges.xRange[0], ranges.xRange[1]]),
        yRange: Object.freeze([ranges.yRange[0], ranges.yRange[1]])
      }) : ranges;
      const { xRange, yRange } = workerRanges;
      const rangesChanged = () => workerRanges !== ranges &&
        (!Object.is(ranges.xRange?.[0], xRange[0]) ||
          !Object.is(ranges.xRange?.[1], xRange[1]) ||
          !Object.is(ranges.yRange?.[0], yRange[0]) ||
          !Object.is(ranges.yRange?.[1], yRange[1]));
      const drawCanvas = () => {
        if (cpuReusePaletteArray && paletteParams) {
          drawMandelbrotFractal(canvas, xRange, yRange, colorFunc,
            colorParams?.useMu !== false, paletteParams);
        } else if (colorParams?.useMu === false) {
          drawMandelbrotFractal(canvas, xRange, yRange, colorFunc, false);
        } else {
          drawMandelbrotFractal(canvas, xRange, yRange, colorFunc);
        }
      };
      const context = canvas.getContext('2d');
      if (!context) { running = false; return; }
      const previous = completed.current;
      const panCanvas = () => colorParams?.useMu === false
        ? applyPanAndRenderEdges(canvas, previous.xRange, previous.yRange, xRange, yRange, colorFunc, false)
        : applyPanAndRenderEdges(canvas, previous.xRange, previous.yRange, xRange, yRange, colorFunc);
      const useWorker = shouldUseWorker(canvas.width, canvas.height);
      const settledRequest = settledPan.current;
      const exactSource = cpuExactPanReuse ? getCpuExactFrame(cpuExactOwner.current) : null;
      const exactPlan = paletteCertifiable && !paletteChanged() && !rangesChanged() &&
        settledRequest && useWorker &&
        exactSource?.sourceKind === 'complete-worker-grid-rgba8' &&
        exactSource.iterationBudget === MAX_ITERATIONS &&
        exactSource.smoothingSteps === SMOOTHING_STEPS &&
        exactSource.paletteStyle === workerColorParams.style &&
        exactSource.width === canvas.width && exactSource.height === canvas.height &&
        exactSource.pixels instanceof Uint8ClampedArray &&
        exactSource.pixels.length === canvas.width * canvas.height * 4 &&
        exactSource.paletteKey === paletteKeyAtStart &&
        exactSource.workerRoute === workerRenderRoute(xRange, yRange, workerColorParams)
        ? planExactCpuPan(exactSource.ranges, workerRanges, canvas.width, canvas.height) : null;
      const reusablePlan = exactPlan && exactPlan.reusablePixels >= exactPlan.totalPixels * 0.5
        ? exactPlan : null;
      let exactPixels = null;
      let exactWork = null;
      let preparedFade = null;
      const prepareFade = () => {
        if (!preparedFade) preparedFade = prepareCPUFade(canvas, ranges);
      };
      try {
        if (reusablePlan) {
          const result = await renderExactPan({ canvasWidth: canvas.width, canvasHeight: canvas.height,
            sourcePixels: exactSource.pixels, dx: reusablePlan.dx, dy: reusablePlan.dy,
            cleanX: reusablePlan.cleanX, cleanY: reusablePlan.cleanY,
            newXRange: xRange, newYRange: yRange, colorParams: workerColorParams });
          if (stale() || rangesChanged()) return;
          if (paletteChanged()) throw new Error('Palette changed during exact CPU pan');
          const pixels = result.pixels;
          if (pixels.length !== canvas.width * canvas.height * 4 ||
              result.computedPixels !== reusablePlan.totalPixels - reusablePlan.reusablePixels) {
            throw new Error('Exact-pan assembled frame count mismatch');
          }
          prepareFade();
          context.putImageData(new ImageData(pixels, canvas.width, canvas.height), 0, 0);
          exactPixels = pixels;
          exactWork = { workerTiles: result.workerTiles, computedPixels: result.computedPixels };
        } else if (pan.current && previous) {
          if (useWorker) {
            const result = await renderPan({ canvasWidth: canvas.width, canvasHeight: canvas.height,
              oldXRange: previous.xRange, oldYRange: previous.yRange,
              newXRange: xRange, newYRange: yRange, colorParams: workerColorParams });
            if (stale() || rangesChanged()) return;
            if (paletteChanged()) throw new Error('Palette changed during CPU pan preview');
            prepareFade();
            presentPanTiles(canvas, context, result);
          } else if (!panCanvas()) {
            prepareFade();
            drawCanvas();
          }
        } else if (useWorker) {
          const pixels = await renderFull({ canvasWidth: canvas.width, canvasHeight: canvas.height,
            xRange, yRange, colorParams: workerColorParams, cpuInteriorDispatch });
          if (stale() || rangesChanged()) return;
          if (paletteChanged()) throw new Error('Palette changed during full CPU render');
          prepareFade();
          context.putImageData(new ImageData(pixels, canvas.width, canvas.height), 0, 0);
          exactPixels = pixels;
        } else {
          prepareFade();
          drawCanvas();
          clearCpuExactFrame(cpuExactOwner.current);
        }
      } catch (error) {
        if (stale() || rangesChanged() || error.name === 'AbortError') return;
        console.warn('Worker render failed, falling back to main thread:', error);
        prepareFade();
        drawCanvas();
        clearCpuExactFrame(cpuExactOwner.current);
      } finally {
        running = false;
      }
      if (!stale() && !rangesChanged()) {
        if (paletteCertifiable && exactPixels && !paletteChanged())
          setCpuExactFrame(cpuExactOwner.current,
          { pixels: exactPixels, ranges: workerRanges, width: canvas.width, height: canvas.height,
            paletteKey: paletteKeyAtStart,
            paletteStyle: workerColorParams.style,
            sourceKind: 'complete-worker-grid-rgba8',
            iterationBudget: MAX_ITERATIONS, smoothingSteps: SMOOTHING_STEPS,
            workerRoute: workerRenderRoute(xRange, yRange, workerColorParams) });
        canvas.style.transform = '';
        cpuPreview.current = false;
        cpuRetainedPreview.current = false;
        completed.current = ranges;
        rendered.current = true;
        completedImage.current = { width: canvas.width, height: canvas.height, colorFunc };
        pan.current = false;
        settledPan.current = false;
        setRecovering(false);
        onCpuFrameReadyRef.current?.(cpuExactPanReuse
          ? { width: canvas.width, height: canvas.height, settledPan: settledRequest,
            exactPan: exactWork ? { reusablePixels: reusablePlan.reusablePixels,
              totalPixels: reusablePlan.totalPixels, dx: reusablePlan.dx, dy: reusablePlan.dy,
              dirtyTransferRGBABytes: exactWork.computedPixels * 4,
              ...exactWork } : null }
          : { width: canvas.width, height: canvas.height });
        if (preparedFade) animateCPUFade(preparedFade);
      }
    };
    canvas.addEventListener('render', render);
    if (canvasEpoch > 0) {
      const rect = canvas.getBoundingClientRect();
      if (rect.bottom >= 0 && rect.top <= window.innerHeight) render();
    }
    return () => {
      disposed = true;
      canvas.removeEventListener('render', render);
      cancelRender();
    };
  }, [canvasRef, canvasEpoch, revision, colorFunc, colorParams, paletteParams,
    cpuReusePaletteArray, cpuExactPanReuse, cpuInteriorDispatch, preferredRenderer,
    shouldUseWorker, renderFull, renderPan, renderExactPan, cancelRender, drawGPU, prepareCPUFade,
    animateCPUFade]);

  return { canvasEpoch, recovering, present, suspend, restoreImage, canCaptureHistory, getPresentedSnapshot };
}
